import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUserProfile } from '../data/repositories/socialRepository';

/**
 * Fetch user profile from Supabase
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User profile or null
 */
async function fetchUserProfile(userId) {
  if (!userId) return null;
  return await getUserProfile(userId);
}

/**
 * Hook to check if user has a profile and load it
 * @param {string} userId - User ID
 * @returns {Object} - { profile, hasProfile, loading, error, refetch }
 */
export function useUserProfile(userId) {
  const {
    data: profile,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['user-profile', userId],
    queryFn: () => fetchUserProfile(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes - profile rarely changes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const hasProfile = useMemo(() => !!profile, [profile]);

  return {
    profile: profile || null,
    hasProfile,
    loading,
    error,
    refetch,
  };
}
